import { CSSProperties, forwardRef, SyntheticEvent } from 'react';
import { twMerge } from 'tailwind-merge';

import { Colors, Sizes } from '@/components/ui/types';

import {
  MenuDirection,
  MenuDropdownProps,
  MenuItemProps,
  MenuProps,
  MenuTitleProps,
} from './types';

const sizeClasses: Record<Sizes, string> = {
  xs: 'menu-xs',
  sm: 'menu-sm',
  md: 'menu-md',
  lg: 'menu-lg',
  xl: 'menu-xl',
};

const directionClasses: Record<MenuDirection, string> = {
  vertical: 'menu-vertical',
  horizontal: 'menu-horizontal',
};

const COLORS_WITH_ACTIVE = [
  'neutral',
  'primary',
  'secondary',
  'accent',
  'info',
  'success',
  'warning',
  'error',
] as const;

const activeColorStyles = {
  default: undefined,
  ...Object.fromEntries(
    COLORS_WITH_ACTIVE.map(color => [
      color,
      {
        '--menu-active-bg': `var(--color-${color})`,
        '--menu-active-fg': `var(--color-${color}-content)`,
      } as CSSProperties,
    ]),
  ),
} as Record<Colors, CSSProperties | undefined>;

const MenuBase = forwardRef<HTMLUListElement, MenuProps>(
  (
    {
      className,
      size = 'md',
      direction = 'vertical',
      activeColor = 'default',
      style,
      children,
      ...props
    },
    ref,
  ) => {
    const classes = twMerge(
      'menu',
      sizeClasses[size],
      directionClasses[direction],
      className,
    );

    return (
      <ul
        ref={ref}
        className={classes}
        style={{ ...activeColorStyles[activeColor], ...style }}
        {...props}
      >
        {children}
      </ul>
    );
  },
);

const Item = forwardRef<HTMLLIElement, MenuItemProps>(
  (
    {
      className,
      active = false,
      disabled = false,
      focus = false,
      activeColor = 'default',
      style,
      children,
      ...props
    },
    ref,
  ) => {
    const classes = twMerge(
      active && 'menu-active',
      disabled && 'menu-disabled opacity-50',
      focus && 'menu-focus',
      className,
    );

    return (
      <li
        ref={ref}
        className={classes}
        style={{ ...activeColorStyles[activeColor], ...style }}
        {...props}
      >
        {children}
      </li>
    );
  },
);

const Title = forwardRef<HTMLLIElement, MenuTitleProps>(
  ({ className, children, ...props }, ref) => (
    <li ref={ref} className={twMerge('menu-title', className)} {...props}>
      {children}
    </li>
  ),
);

const Dropdown = forwardRef<HTMLLIElement, MenuDropdownProps>(
  ({ label, classNames, open, onToggle, children, ...props }, ref) => {
    const handleToggle = (e: SyntheticEvent<HTMLDetailsElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onToggle?.(!open);
    };

    return (
      <li ref={ref} className={classNames?.li} {...props}>
        <details
          open={open}
          onClick={handleToggle}
          className={classNames?.details}
        >
          <summary className={classNames?.summary}>{label}</summary>
          <ul>{children}</ul>
        </details>
      </li>
    );
  },
);

MenuBase.displayName = 'Menu';
Item.displayName = 'Menu.Item';
Title.displayName = 'Menu.Title';
Dropdown.displayName = 'Menu.Dropdown';

export const Menu = Object.assign(MenuBase, {
  Item,
  Title,
  Dropdown,
});
